/**
 * WhatsNewAdmin already exists — this new page lets admins toggle
 * runtime feature flags without a redeploy. Currently just gates the
 * Directory revival; new flags added to `DEFAULT_FLAGS` in
 * `feature_flags_routes.py` show up here automatically.
 */
import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../../App';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Switch } from '../../components/ui/switch';
import { Flag, Check, X } from 'lucide-react';

const FeatureFlagsAdmin = () => {
  const { API } = useContext(AuthContext);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/feature-flags`);
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load flags");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-line */ }, [API]);

  const toggle = async (row) => {
    setSaving(row.key);
    try {
      const next = !row.enabled;
      await axios.put(`${API}/admin/feature-flags/${row.key}`, { enabled: next });
      toast.success(`${row.key} ${next ? 'enabled' : 'disabled'}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Toggle failed');
    } finally { setSaving(null); }
  };

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-5" data-testid="feature-flags-admin">
      <header>
        <h1 className="font-heading text-3xl font-extrabold flex items-center gap-2">
          <Flag className="w-6 h-6 text-primary" /> Feature flags
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Runtime toggles — no redeploy needed. Register new flags in <code>feature_flags_routes.py</code>.
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Registered flags</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-sm text-muted-foreground">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="text-sm text-muted-foreground">No flags registered.</div>
          ) : (
            <ul className="divide-y divide-border" data-testid="feature-flags-list">
              {rows.map((r) => (
                <li key={r.key} className="py-3 flex items-center gap-3" data-testid={`feature-flag-row-${r.key}`}>
                  <div className="min-w-0 flex-1">
                    <div className="font-bold text-sm flex items-center gap-2">
                      <code>{r.key}</code>
                      {r.enabled ? (
                        <span className="inline-flex items-center gap-0.5 text-[9px] uppercase tracking-widest font-black bg-emerald-500/20 text-emerald-800 rounded-full px-1.5 py-0.5">
                          <Check className="w-2.5 h-2.5" /> On
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-0.5 text-[9px] uppercase tracking-widest font-black bg-rose-500/15 text-rose-700 rounded-full px-1.5 py-0.5">
                          <X className="w-2.5 h-2.5" /> Off
                        </span>
                      )}
                    </div>
                    {r.updated_at && (
                      <div className="text-[10px] text-muted-foreground mt-0.5">
                        Last updated {new Date(r.updated_at).toLocaleString()}
                        {r.updated_by && ` · by ${r.updated_by}`}
                      </div>
                    )}
                  </div>
                  <Switch
                    checked={!!r.enabled}
                    disabled={saving === r.key}
                    onCheckedChange={() => toggle(r)}
                    data-testid={`feature-flag-toggle-${r.key}`}
                  />
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default FeatureFlagsAdmin;
